<template>
    <dialog ref="expandedDialog" class="expanded-editor-dialog" aria-label="Editor ampliado" @cancel.prevent="closeExpanded" @close="expanded = false"></dialog>
    <Teleport :to="expanded ? $refs.expandedDialog : 'body'" :disabled="!expanded">
    <section ref="editorSection" class="feedback-editor mb-3" :class="{ 'editor-expanded': expanded }">
        <div class="editor-heading">
            <span class="form-label mb-0 fw-bold">Feedback de la pregunta</span>
            <div class="editor-heading-actions">
            <button ref="expandButton" type="button" class="btn btn-sm btn-outline-secondary" :aria-expanded="expanded" @click="expanded ? closeExpanded() : openExpanded()">
                <i :class="expanded ? 'bi bi-arrows-angle-contract' : 'bi bi-arrows-angle-expand'" aria-hidden="true"></i>
                {{ expanded ? 'Terminar edición' : 'Ampliar editor' }}
            </button>
            <button type="button" class="btn btn-sm btn-outline-secondary" :aria-pressed="preview" @click="togglePreview">
                {{ preview ? 'Editar' : 'Vista previa' }}
            </button>
            </div>
        </div>
        <p class="small text-secondary mb-2">Explicación opcional que se muestra con el resultado.</p>
        <p v-if="busy" role="status" class="small mb-2">Procesando imagen…</p>
        <p v-if="error" role="alert" class="text-danger mb-2">{{ error }}</p>
        <div v-show="!preview" class="editor-workspace">
            <div class="editor-toolbar" role="group" aria-label="Formato del feedback">
                <button v-for="tool in tools.slice(0, 3)" :key="tool.command" type="button" :title="tool.label" :aria-label="tool.label" :aria-pressed="activeFormats[tool.command] || false" :disabled="busy"
                    @mousedown.prevent @click="format(tool.command)"><i :class="`bi bi-${tool.icon}`" aria-hidden="true"></i></button>
                <select aria-label="Tamaño del texto" title="Tamaño del texto" :value="currentSize" :disabled="busy" @focus="rememberSelection" @change="format('fontSize', $event.target.value)">
                    <option value="1">10 px</option><option value="2">13 px</option><option value="3">16 px</option><option value="4">18 px</option><option value="5">24 px</option><option value="6">32 px</option><option value="7">48 px</option>
                </select>
                <button v-for="align in alignments" :key="align.command" type="button" :title="align.label" :aria-label="align.label"
                    :aria-pressed="currentAlignment === align.command" :disabled="busy || (!!selectedImage && align.command === 'justifyFull')"
                    @mousedown.prevent @click="format(align.command)"><i :class="`bi bi-${align.icon}`" aria-hidden="true"></i></button>
                <button v-if="selectedImage" type="button" title="Restablecer tamaño de imagen" aria-label="Restablecer tamaño de imagen" :disabled="busy" @mousedown.prevent @click="resetImageSize"><i class="bi bi-aspect-ratio" aria-hidden="true"></i></button>
                <input type="color" aria-label="Color del texto" title="Color del texto" value="#172033" :disabled="busy" @pointerdown="rememberSelection" @input="format('foreColor', $event.target.value)">
                <button type="button" title="Subir imagen" aria-label="Subir imagen" :disabled="busy" @mousedown.prevent @click="chooseImage"><i class="bi bi-image" aria-hidden="true"></i></button>
                <button type="button" title="Imagen por enlace" aria-label="Imagen por enlace" :disabled="busy" :aria-expanded="showImageUrl" @mousedown.prevent @click="openImageUrl"><i class="bi bi-image-alt" aria-hidden="true"></i></button>
                <input ref="file" type="file" accept="image/png,image/jpeg,image/webp,image/gif" hidden @change="fileChanged">
                <button type="button" title="Insertar / editar enlace" aria-label="Insertar / editar enlace" :disabled="busy" @mousedown.prevent @click="openLink()"><i class="bi bi-link-45deg" aria-hidden="true"></i></button>
                <button type="button" title="Más herramientas" aria-label="Más herramientas" :aria-expanded="showMore" @mousedown.prevent @click="showMore = !showMore"><i class="bi bi-three-dots" aria-hidden="true"></i></button>
            </div>
            <div v-if="showMore" class="editor-toolbar extra-tools" role="group" aria-label="Más formato">
                <button v-for="tool in tools.slice(3)" :key="tool.command" type="button" :title="tool.label" :aria-label="tool.label" :disabled="busy" @mousedown.prevent @click="format(tool.command)"><i :class="`bi bi-${tool.icon}`" aria-hidden="true"></i></button>
                <button type="button" title="Tachado" aria-label="Tachado" :disabled="busy" @mousedown.prevent @click="format('strikeThrough')"><s>S</s></button>
                <select aria-label="Estilo de párrafo" :disabled="busy" @focus="rememberSelection" @change="format('formatBlock', $event.target.value)">
                    <option value="p">Párrafo</option><option value="h2">Título</option><option value="h3">Subtítulo</option><option value="blockquote">Cita</option>
                </select>
                <input type="color" aria-label="Color de resaltado" title="Color de resaltado" value="#fff59d" :disabled="busy" @pointerdown="rememberSelection" @input="format('hiliteColor', $event.target.value)">
                <button type="button" title="Quitar enlace" aria-label="Quitar enlace" :disabled="busy" @mousedown.prevent @click="format('unlink')"><i class="bi bi-link" aria-hidden="true"></i> ×</button>
                <button type="button" title="Activar / quitar texto en movimiento" :disabled="busy" @mousedown.prevent @click="toggleMarquee">Marquee</button>
            </div>
            <div v-if="showLink" class="image-url-controls p-2 border">
                <label class="d-block w-100">Texto del enlace
                    <input v-model="linkText" class="form-control" type="text" placeholder="Texto que se mostrará"
                        @keydown.enter.prevent="insertLink" @keydown.esc.prevent="showLink = false">
                </label>
                <label class="d-block w-100">Dirección del enlace
                    <input ref="linkUrl" v-model="linkUrl" class="form-control" type="text" inputmode="url" placeholder="https://ejemplo.com"
                        @keydown.enter.prevent="insertLink" @keydown.esc.prevent="showLink = false">
                </label>
                <label class="d-block w-100">Abrir enlace en
                    <select v-model="linkTarget" class="form-select">
                        <option value="_self">La misma pestaña</option>
                        <option value="_blank">Una pestaña nueva</option>
                    </select>
                </label>
                <button type="button" class="btn btn-primary btn-sm" :disabled="busy" @click="insertLink">Aplicar enlace</button>
                <button type="button" class="btn btn-outline-secondary btn-sm" @click="showLink = false">Cancelar</button>
            </div>
            <div v-if="showImageUrl" class="image-url-controls p-2 border">
                <label class="d-block w-100">Enlace directo de la imagen
                    <input ref="imageUrl" v-model="imageUrl" type="text" inputmode="url" class="form-control" placeholder="https://ejemplo.com/imagen.jpg"
                        :disabled="busy" @keydown.enter.prevent="addImageUrl(imageUrl)" @keydown.esc.prevent="showImageUrl = false">
                </label>
                <button type="button" class="btn btn-primary btn-sm" :disabled="busy || !imageUrl.trim()" @click="addImageUrl(imageUrl)">Insertar imagen</button>
                <button type="button" class="btn btn-outline-secondary btn-sm" :disabled="busy" @click="showImageUrl = false">Cancelar</button>
            </div>
            <div ref="imageFrame" class="image-frame">
                <div v-if="selectionMenu && !busy && !showLink && !showImageUrl" class="selection-menu" role="toolbar" aria-label="Formato de la selección" :style="selectionMenu">
                    <button v-for="tool in tools.slice(0, 3)" :key="tool.command" type="button" :title="tool.label" :aria-label="tool.label" :aria-pressed="activeFormats[tool.command] || false" @mousedown.prevent @click="format(tool.command)"><i :class="`bi bi-${tool.icon}`" aria-hidden="true"></i></button>
                    <button type="button" title="Editar enlace de la selección" aria-label="Editar enlace de la selección" @mousedown.prevent @click="openLink()"><i class="bi bi-link-45deg" aria-hidden="true"></i></button>
                    <button type="button" title="Cerrar menú contextual" aria-label="Cerrar menú contextual" @mousedown.prevent @click="selectionMenu = null">×</button>
                </div>
            <div ref="editor" class="editor-surface" :contenteditable="!busy" role="textbox" aria-multiline="true" aria-label="Feedback de la pregunta"
                data-placeholder="Escribe la explicación…" @input="changed" @blur="rememberSelection" @keyup="rememberSelection" @mouseup="rememberSelection"
                @click="selectImage" @keydown.esc="clearImage" @load.capture="updateImageBox"
                @paste.prevent="paste" @drop.prevent @dragover.prevent></div>
                <div v-if="imageBox && !busy" class="image-resize-box" :style="imageBox">
                    <button v-for="corner in ['nw', 'ne', 'sw', 'se']" :key="corner" type="button" :class="['resize-handle', corner]"
                        :aria-label="`Cambiar tamaño de imagen (${corner})`" @pointerdown.prevent="startResize($event, corner)"
                        @pointermove.prevent="resizeImage" @pointerup="endResize" @pointercancel="endResize" @lostpointercapture="endResize"
                        @keydown.left.prevent="stepImage(-10)" @keydown.right.prevent="stepImage(10)" @keydown.esc="clearImage"></button>
                </div>
            </div>
        </div>
        <div v-if="preview" class="editor-preview">
            <FeedbackContent :value="modelValue" />
            <span v-if="!modelValue" class="text-secondary">Todavía no hay feedback.</span>
        </div>
    </section>
    </Teleport>
</template>

<script>
import FeedbackContent from './FeedbackContent.vue';
import { sanitizeFeedback, safeImageSource, safeLinkHref, prepareFeedbackImage, MAX_FEEDBACK_LENGTH } from '@/utils/feedback-html';

export default {
    components: { FeedbackContent },
    props: { modelValue: { type: String, default: '' } },
    emits: ['update:modelValue'],
    data: () => ({
        preview: false, busy: false, error: '', selection: null, lastValue: '', disposed: false,
        showImageUrl: false, imageUrl: '',
        showLink: false, linkUrl: '', selectedImage: null, imageBox: null, resizeState: null,
        linkText: '', linkTarget: '_blank', editingLink: null,
        showMore: false, selectionMenu: null, activeFormats: {},
        currentSize: '3', currentAlignment: 'justifyLeft',
        expanded: false,
        imageAlignment: 'left',
        alignments: [
            { command: 'justifyLeft', label: 'Alinear a la izquierda', icon: 'text-left' },
            { command: 'justifyCenter', label: 'Centrar', icon: 'text-center' },
            { command: 'justifyRight', label: 'Alinear a la derecha', icon: 'text-right' },
            { command: 'justifyFull', label: 'Justificar', icon: 'justify' },
        ],
        tools: [
            { command: 'bold', label: 'Negrita', icon: 'type-bold' },
            { command: 'italic', label: 'Cursiva', icon: 'type-italic' },
            { command: 'underline', label: 'Subrayado', icon: 'type-underline' },
            { command: 'insertUnorderedList', label: 'Lista con viñetas', icon: 'list-ul' },
            { command: 'insertOrderedList', label: 'Lista numerada', icon: 'list-ol' },
            { command: 'removeFormat', label: 'Quitar formato', icon: 'eraser' },
            { command: 'undo', label: 'Deshacer', icon: 'arrow-counterclockwise' },
            { command: 'redo', label: 'Rehacer', icon: 'arrow-clockwise' },
        ],
    }),
    watch: {
        modelValue(value) {
            // Don't rewrite the active DOM on each keystroke: that would lose
            // the cursor, IME composition and the browser's undo history.
            if (value !== this.lastValue) this.load(value);
        },
    },
    mounted() {
        this.load(this.modelValue);
        document.addEventListener('selectionchange', this.rememberSelection);
        this.resizeObserver = new ResizeObserver(this.updateImageBox);
        this.resizeObserver.observe(this.$refs.editor);
    },
    beforeUnmount() { this.disposed = true; this.$refs.expandedDialog.close(); this.resizeObserver.disconnect(); document.removeEventListener('selectionchange', this.rememberSelection); },
    methods: {
        async openExpanded() {
            this.rememberSelection();
            this.expanded = true;
            await this.$nextTick();
            this.$refs.expandedDialog.showModal();
            if (!this.preview) this.restoreSelection();
            this.updateImageBox();
        },
        async closeExpanded() {
            this.rememberSelection();
            this.$refs.expandedDialog.close();
            this.expanded = false;
            await this.$nextTick();
            this.$refs.expandButton?.focus();
            this.updateImageBox();
        },
        clearImage() { this.selectedImage = null; this.imageBox = null; this.resizeState = null; },
        selectImage(event) {
            // Links stay editable here; they open in the preview/result.
            const link = event.target.closest('a');
            if (link && event.target.tagName !== 'IMG') { event.preventDefault(); this.openLink(link); return; }
            if (link) event.preventDefault();
            this.selectedImage = event.target.tagName === 'IMG' ? event.target : null;
            this.updateImageBox();
        },
        updateImageBox() {
            const img = this.selectedImage;
            if (!img || !this.$refs.editor?.contains(img)) { this.clearImage(); return; }
            this.imageAlignment = img.getAttribute('data-align') || 'left';
            this.currentAlignment = {left: 'justifyLeft', center: 'justifyCenter', right: 'justifyRight'}[this.imageAlignment];
            const rect = img.getBoundingClientRect();
            const frame = this.$refs.imageFrame.getBoundingClientRect();
            this.imageBox = { left: `${rect.left - frame.left}px`, top: `${rect.top - frame.top}px`, width: `${rect.width}px`, height: `${rect.height}px` };
        },
        startResize(event, corner) {
            if (!this.selectedImage || (event.pointerType === 'mouse' && event.button !== 0)) return;
            const rect = this.selectedImage.getBoundingClientRect();
            this.resizeState = { id: event.pointerId, x: event.clientX, y: event.clientY, width: rect.width, ratio: rect.width / rect.height, corner };
            event.currentTarget.setPointerCapture(event.pointerId);
        },
        resizeImage(event) {
            const state = this.resizeState;
            if (!state || state.id !== event.pointerId) return;
            const dx = (event.clientX - state.x) * (state.corner.endsWith('w') ? -1 : 1);
            const dy = (event.clientY - state.y) * (state.corner.startsWith('n') ? -1 : 1) * state.ratio;
            this.setImageWidth(state.width + (Math.abs(dx) >= Math.abs(dy) ? dx : dy));
        },
        setImageWidth(width) {
            if (!this.selectedImage) return;
            const max = Math.min(1600, this.$refs.editor.clientWidth - 28);
            this.selectedImage.setAttribute('width', String(Math.round(Math.max(32, Math.min(max, width)))));
            this.changed();
            this.updateImageBox();
        },
        alignImage(align) {
            if (this.busy || !this.selectedImage || !this.$refs.editor.contains(this.selectedImage) || !['left', 'center', 'right'].includes(align)) return;
            this.selectedImage.setAttribute('data-align', align);
            this.changed();
            this.updateImageBox();
        },
        stepImage(delta) { if (this.selectedImage) this.setImageWidth(this.selectedImage.getBoundingClientRect().width + delta); },
        endResize() { this.resizeState = null; },
        resetImageSize() { if (this.selectedImage) { this.selectedImage.removeAttribute('width'); this.changed(); this.updateImageBox(); } },
        async openLink(link = null) {
            if (this.busy) return;
            this.rememberSelection();
            const container = this.selection?.commonAncestorContainer;
            const element = container?.nodeType === 1 ? container : container?.parentElement;
            link = link || element?.closest('a');
            if (link && !this.$refs.editor.contains(link)) link = null;
            this.editingLink = link;
            if (link) {
                const range = document.createRange();
                range.selectNode(link);
                this.selection = range;
            }
            this.clearImage();
            this.showLink = true;
            this.linkUrl = link?.getAttribute('href') || '';
            this.linkText = link?.textContent || this.selection?.toString() || '';
            this.linkTarget = link?.getAttribute('target') === '_self' ? '_self' : '_blank';
            this.error = '';
            await this.$nextTick();
            this.$refs.linkUrl.focus();
        },
        insertLink() {
            if (this.busy) return;
            const href = safeLinkHref(this.linkUrl);
            if (!href) { this.error = 'Introduce un enlace http://, https:// o mailto: válido.'; return; }
            try {
                const link = document.createElement('a');
                link.href = href;
                link.target = this.linkTarget === '_self' ? '_self' : '_blank';
                if (link.target === '_blank') link.rel = 'noopener noreferrer';
                if (this.editingLink && this.$refs.editor.contains(this.editingLink)) {
                    const range = document.createRange();
                    range.selectNode(this.editingLink);
                    this.selection = range;
                    // Keep emphasis and embedded images when only the URL/target changes.
                    if (this.linkText === this.editingLink.textContent) link.innerHTML = this.editingLink.innerHTML;
                }
                if (!link.hasChildNodes()) link.textContent = this.linkText.trim() || href;
                this.insert(sanitizeFeedback(link.outerHTML));
                this.editingLink = null;
                this.showLink = false;
                this.error = '';
            } catch (error) { this.error = error.message; }
        },
        readyForSave() {
            if (!this.busy) return true;
            this.error = 'Espera a que termine de procesarse la imagen antes de guardar.';
            return false;
        },
        load(value) {
            this.selectionMenu = null;
            this.clearImage();
            this.showLink = false;
            this.editingLink = null;
            this.lastValue = sanitizeFeedback(value);
            if (this.$refs.editor) this.$refs.editor.innerHTML = this.lastValue;
            this.selection = null;
            this.showImageUrl = false;
            this.imageUrl = '';
        },
        rememberSelection() {
            const selection = window.getSelection();
            if (selection.rangeCount && this.$refs.editor?.contains(selection.getRangeAt(0).commonAncestorContainer)) {
                this.selection = selection.getRangeAt(0).cloneRange();
                this.activeFormats = Object.fromEntries(['bold', 'italic', 'underline'].map(command => [command, document.queryCommandState(command)]));
                const node = selection.anchorNode;
                const style = getComputedStyle(node.nodeType === 1 ? node : node.parentElement);
                const sizeIndex = [10, 13, 16, 18, 24, 32, 48].indexOf(parseFloat(style.fontSize));
                this.currentSize = String(sizeIndex < 0 ? 3 : sizeIndex + 1);
                if (!this.selectedImage) this.currentAlignment = {center: 'justifyCenter', right: 'justifyRight', justify: 'justifyFull'}[style.textAlign] || 'justifyLeft';
                const rect = this.selection.getBoundingClientRect();
                const frame = this.$refs.imageFrame.getBoundingClientRect();
                this.selectionMenu = !selection.isCollapsed && selection.toString().trim() ? {
                    left: `${Math.max(0, Math.min(frame.width - 200, rect.left - frame.left))}px`,
                    top: `${Math.max(0, rect.top - frame.top - 46)}px`,
                } : null;
            } else if (!this.$refs.editorSection.contains(document.activeElement)) {
                this.selectionMenu = null;
            }
        },
        restoreSelection() {
            const editor = this.$refs.editor;
            editor.focus();
            const selection = window.getSelection();
            const range = this.selection && editor.contains(this.selection.commonAncestorContainer) ? this.selection : document.createRange();
            if (range !== this.selection) { range.selectNodeContents(editor); range.collapse(false); }
            selection.removeAllRanges();
            selection.addRange(range);
        },
        changed() {
            const html = this.$refs.editor.innerHTML;
            if (html.length > MAX_FEEDBACK_LENGTH) {
                this.$refs.editor.innerHTML = this.lastValue;
                this.selection = null;
                this.error = 'El feedback no puede superar 2 MB. Elimina alguna imagen antes de añadir más contenido.';
                return;
            }
            this.lastValue = sanitizeFeedback(html);
            this.$emit('update:modelValue', this.lastValue);
            this.rememberSelection();
            this.$nextTick(this.updateImageBox);
        },
        format(command, value = null) {
            if (this.busy) return;
            if (this.selectedImage && command.startsWith('justify')) {
                this.alignImage({justifyLeft: 'left', justifyCenter: 'center', justifyRight: 'right'}[command]);
                return;
            }
            this.error = '';
            this.restoreSelection();
            // Native editing keeps selection and undo/redo on desktop/mobile.
            // Isolated here so the deprecated editing API can be replaced later.
            document.execCommand(command, false, value);
            this.changed();
        },
        toggleMarquee() {
            if (this.busy) return;
            this.restoreSelection();
            const container = this.selection?.commonAncestorContainer;
            const element = container?.nodeType === 1 ? container : container?.parentElement;
            const marquee = element?.closest('[data-marquee]');
            if (marquee && this.$refs.editor.contains(marquee)) {
                marquee.removeAttribute('data-marquee');
                marquee.querySelectorAll('[data-marquee-text]').forEach(node => node.removeAttribute('data-marquee-text'));
                this.changed();
            } else if (this.selection && this.selection.toString().trim()) {
                const block = document.createElement('div'); block.setAttribute('data-marquee', 'true');
                const text = document.createElement('div'); text.setAttribute('data-marquee-text', 'true'); text.appendChild(this.selection.cloneContents()); block.appendChild(text);
                this.insert(block.outerHTML);
            } else { this.error = 'Selecciona el texto que quieres poner en movimiento.'; }
        },
        insert(html) {
            if (this.$refs.editor.innerHTML.length + html.length > MAX_FEEDBACK_LENGTH) {
                throw new Error('El feedback no puede superar 2 MB. Elimina alguna imagen antes de añadir más contenido.');
            }
            this.restoreSelection();
            document.execCommand('insertHTML', false, html);
            this.changed();
        },
        chooseImage() { this.rememberSelection(); this.$refs.file.click(); },
        async openImageUrl() {
            this.rememberSelection();
            this.showImageUrl = true;
            this.error = '';
            await this.$nextTick();
            this.$refs.imageUrl.focus();
        },
        async addImageUrl(value) {
            if (this.busy) return;
            this.error = '';
            const src = safeImageSource(value);
            if (!src || (!src.startsWith('https://') && !src.startsWith('/'))) {
                this.error = 'Introduce un enlace HTTPS directo a una imagen o una ruta local que empiece por /.';
                return;
            }
            this.busy = true;
            const originalValue = this.lastValue;
            try {
                const img = new Image();
                img.alt = 'Imagen del feedback';
                await new Promise((resolve, reject) => {
                    const timeout = setTimeout(() => finish(new Error('La imagen tarda demasiado en cargar. Comprueba el enlace.')), 10000);
                    const finish = error => {
                        clearTimeout(timeout);
                        img.onload = img.onerror = null;
                        error ? reject(error) : resolve();
                    };
                    img.onload = () => finish();
                    img.onerror = () => finish(new Error('No se ha podido cargar la imagen. Usa un enlace directo a una imagen accesible.'));
                    img.src = src;
                });
                if (this.disposed || this.lastValue !== originalValue) return;
                this.busy = false;
                await this.$nextTick();
                this.insert(img.outerHTML + '<p><br></p>');
                this.showImageUrl = false;
                this.imageUrl = '';
            } catch (error) { if (!this.disposed) this.error = error.message; }
            finally { this.busy = false; }
        },
        async fileChanged(event) {
            const file = event.target.files[0];
            event.target.value = '';
            if (file) await this.addImage(file);
        },
        async addImage(file) {
            if (this.busy) return;
            this.rememberSelection();
            this.busy = true;
            this.error = '';
            try {
                const src = await prepareFeedbackImage(file);
                if (this.disposed) return;
                // Restore contenteditable before focusing after asynchronous decoding.
                this.busy = false;
                await this.$nextTick();
                const img = document.createElement('img');
                img.src = src;
                img.alt = file.name || 'Imagen del feedback';
                this.insert(img.outerHTML + '<p><br></p>');
            } catch (error) { this.error = error.message || 'No se ha podido leer la imagen.'; }
            finally { this.busy = false; }
        },
        async paste(event) {
            if (this.busy) return;
            this.rememberSelection();
            this.error = '';
            const clipboard = event.clipboardData;
            const image = Array.from(clipboard.files || []).find(file => file.type.startsWith('image/'));
            if (image) { await this.addImage(image); return; }
            try {
                const html = clipboard.getData('text/html');
                const text = clipboard.getData('text/plain');
                if ((html || text).length > MAX_FEEDBACK_LENGTH) throw new Error('El contenido pegado es demasiado grande (máximo 2 MB).');
                const src = safeImageSource(text.trim());
                if (src && /\.(png|jpe?g|webp|gif|avif)$/i.test(new URL(src, window.location.origin).pathname)) {
                    await this.addImageUrl(src);
                    return;
                }
                const escaped = document.createElement('div');
                escaped.textContent = text;
                if (!html && safeLinkHref(text.trim())) {
                    const link = document.createElement('a');
                    link.href = safeLinkHref(text.trim());
                    link.textContent = text.trim();
                    this.insert(link.outerHTML);
                    return;
                }
                this.insert(html ? sanitizeFeedback(html) : escaped.innerHTML.replace(/\r?\n/g, '<br>'));
            } catch (error) { this.error = error.message; }
        },
        togglePreview() { this.rememberSelection(); this.preview = !this.preview; },
    },
};
</script>

<style scoped>
.feedback-editor { border-top: 1px solid var(--vc-line-strong, #cbd5e1); padding-top: 16px; }
.editor-heading { display: flex; align-items: center; justify-content: space-between; gap: 12px; margin-bottom: 6px; }
.editor-heading { flex-wrap: wrap; }
.editor-heading-actions { display: flex; flex-wrap: wrap; gap: 6px; }
.expanded-editor-dialog { width: min(1200px, 94vw); max-width: 94vw; height: 88dvh; max-height: 92dvh; padding: 18px; border: 1px solid #cbd5e1; border-radius: 12px; background: var(--vc-paper-50, white); color: var(--vc-text-900, #172033); }
.expanded-editor-dialog::backdrop { background: #0f172a99; }
.editor-expanded { display: flex; flex-direction: column; min-height: 100%; border-top: 0; padding-top: 0; margin-bottom: 0 !important; }
.editor-expanded .editor-heading { position: sticky; top: -18px; z-index: 8; background: var(--vc-paper-50, white); padding: 8px 0; }
.editor-expanded .editor-workspace { flex: 1; display: flex; flex-direction: column; }
.editor-expanded .image-frame { flex: 1; display: flex; flex-direction: column; }
.editor-expanded .editor-surface, .editor-expanded .editor-preview { flex: 1; min-height: 50dvh; }
.editor-toolbar { display: flex; flex-wrap: wrap; gap: 4px; padding: 6px; background: var(--vc-paper-200, #f1f5f9); border: 1px solid var(--vc-line-strong, #cbd5e1); border-radius: 8px 8px 0 0; }
.editor-toolbar button { min-width: 44px; min-height: 44px; border: 1px solid transparent; border-radius: 6px; background: transparent; color: var(--vc-text-900, #172033); touch-action: manipulation; }
.editor-toolbar button:hover, .editor-toolbar button:focus-visible { background: var(--vc-paper-50, white); border-color: #64748b; }
.editor-toolbar { gap: 2px; padding: 4px; align-items: center; }
.editor-toolbar select { max-width: 125px; min-height: 36px; border: 1px solid #cbd5e1; border-radius: 5px; background: white; font-size: 13px; }
.editor-toolbar input[type=color] { width: 32px; height: 32px; padding: 2px; border: 1px solid #cbd5e1; border-radius: 5px; cursor: pointer; }
.editor-toolbar button[aria-pressed=true], .selection-menu button[aria-pressed=true] { background: #dbeafe; color: #123e77; }
.extra-tools { border-radius: 0; border-top: 0; }
.selection-menu { position: absolute; z-index: 5; display: flex; background: #172033; border-radius: 8px; padding: 2px; box-shadow: 0 3px 12px #0003; }
.selection-menu button { width: 38px; height: 40px; padding: 0; border: 0; border-radius: 5px; color: white; background: transparent; }
.selection-menu button:focus-visible, .selection-menu button:hover { background: #334155; }
.editor-surface :deep([data-marquee]) { border-inline-start: 3px solid #2373b9; padding-inline-start: 8px; background: #eff6ff; }
@media (pointer: coarse) {
    .editor-toolbar select, .editor-toolbar input[type=color], .selection-menu button { min-height: 44px; }
    .selection-menu button { width: 40px; }
}
.image-url-controls { display: flex; flex-wrap: wrap; gap: 8px; }
.editor-surface, .editor-preview { min-height: 180px; padding: 14px; border: 1px solid var(--vc-line-strong, #cbd5e1); border-radius: 0 0 8px 8px; background: var(--vc-paper-50, white); color: var(--vc-text-900, #172033); overflow-wrap: anywhere; line-height: 1.6; white-space: pre-wrap; }
.editor-surface:focus { outline: 2px solid #2373b9; outline-offset: -2px; }
.editor-surface:empty::before { content: attr(data-placeholder); color: #64748b; pointer-events: none; }
.editor-surface :deep(img) { display: block; max-width: 100%; max-height: 300px; height: auto; margin: 12px 0; }
.editor-surface :deep(p:last-child) { margin-bottom: 0; }
.editor-surface :deep(img[width]) { max-height: none; }
.editor-surface :deep(img[data-align=left]) { margin-left: 0; margin-right: auto; }
.editor-surface :deep(img[data-align=center]) { margin-left: auto; margin-right: auto; }
.editor-surface :deep(img[data-align=right]) { margin-left: auto; margin-right: 0; }
.editor-surface :deep(a) { color: #1263a1; text-decoration: underline; }
.image-frame { position: relative; }
.image-resize-box { position: absolute; border: 2px solid #2373b9; pointer-events: none; box-sizing: border-box; }
.resize-handle { position: absolute; width: 24px; height: 24px; padding: 0; border: 2px solid #fff; border-radius: 4px; background: #2373b9; pointer-events: auto; touch-action: none; }
.resize-handle.nw { left: -12px; top: -12px; cursor: nwse-resize; }
.resize-handle.ne { right: -12px; top: -12px; cursor: nesw-resize; }
.resize-handle.sw { left: -12px; bottom: -12px; cursor: nesw-resize; }
.resize-handle.se { right: -12px; bottom: -12px; cursor: nwse-resize; }
</style>
